import { action, computed, makeObservable, observable } from 'mobx';
import { NodeBaseModel } from '..';
import { AccountingStatementInputDto, DataType, SourceFetchConditionDto } from '../../types';
import { TypeUtils } from '../../../common/misc';
import { SourceFetchConditionValidator } from '../../misc';
import { Utils } from '../../../custom_shared/misc';

export default abstract class InputBaseModel {
    readonly id: string;
    readonly guid: string;
    readonly name: string;
    readonly parentInputId: string | null;
    readonly initialValue: unknown;
    readonly meta: Record<string, unknown>;
    readonly sourceId: string | null;
    readonly sourceFetchCondition: SourceFetchConditionDto | null;
    readonly dataType: DataType;
    readonly required: boolean;
    readonly isReadOnly: boolean;

    value: unknown;
    isModified: boolean;

    constructor(
        readonly node: NodeBaseModel,
        data: AccountingStatementInputDto
    ) {
        this.id = data.id;
        this.guid = data.guid;
        this.name = data.name;
        this.value = this.getValue(data.value);
        this.initialValue = this.getValue(data.initialValue);
        this.meta = data.meta;
        this.sourceId = data.sourceId;
        this.sourceFetchCondition = data.sourceFetchCondition;
        this.dataType = data.dataType;
        this.required = data.required;
        this.isReadOnly = data.isReadOnly;
        this.isModified = data.isModified;

        makeObservable(this, {
            value: observable,
            isModified: observable,
            setValue: action.bound,
            setIsModified: action.bound,
            hasValue: computed
        });
    }

    setValue(value: unknown) {
        this.value = this.getValue(value);
        this.node.inputValueChangeSubject.next(this);
    }

    setIsModified(isModified: boolean) {
        this.isModified = isModified;
    }

    validate() {
        this.node.inputValidationSubject.next(this);
    }

    get hasValue() {
        if (TypeUtils.isString(this.value) || TypeUtils.isArray(this.value)) {
            return this.value.length > 0;
        }

        return this.value !== null;
    }

    get worksheetId() {
        return this.node.worksheet.id;
    }

    get dto(): AccountingStatementInputDto {
        return {
            id: this.id,
            guid: this.guid,
            name: this.name,
            value:
                this.dataType === 'DateTime'
                    ? Utils.convertDateValueToString(this.value, { applyStartOfDay: true })
                    : this.value,
            initialValue: this.initialValue,
            meta: this.meta,
            sourceId: this.sourceId,
            sourceFetchCondition: this.sourceFetchCondition,
            dataType: this.dataType,
            required: this.required,
            isReadOnly: this.isReadOnly,
            isModified: this.isModified
        };
    }

    get nodeId() {
        return this.node.id;
    }

    update(data: AccountingStatementInputDto) {
        this.setValue(data.value);
    }

    invalidateDependentInputsSources() {
        const dependentInputs = SourceFetchConditionValidator.getDependentInputs(this);
        dependentInputs.forEach(i => i.setHasSourceOptionsLoaded(false));
    }

    private getValue(value: unknown) {
        return value === undefined ? null : value;
    }
}
